/** Validación pura de slug de negocio (formato + reservados). Sin DB. */

// Regla pública (signup, /api/check-slug, abrir-negocio): 3-30 chars en
// minúsculas/números/guiones, empezando Y terminando en alfanumérico. El
// formulario nunca produce guiones en los bordes (toSlug los recorta), pero un
// POST directo podía registrar "---" o "-foo-".
export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/

// Regla laxa para altas desde el panel superadmin (finalizeAgentOnboarding):
// mismo charset, pero 2-50 chars — el wizard de agente siempre permitió mínimo
// 2 y existen slugs históricos de SA de hasta 50. Solo aplica a altas NUEVAS,
// así que las cuentas existentes no se ven afectadas.
export const AGENT_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$/

/**
 * Identidad normalizada: lo que queda del slug al quitar todo lo que no se
 * puede dictar por teléfono (guiones, acentos, mayúsculas). Dos slugs con la
 * misma forma normalizada son EL MISMO negocio — la unicidad en DB
 * (merchants/locations.slug_norm) y la resolución tolerante usan esta forma.
 * Debe mantenerse en espejo con la expresión SQL de la columna generada:
 * regexp_replace(lower(slug), '[^a-z0-9]', '', 'g').
 */
export function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "")
}

export const RESERVED_SLUGS = new Set([
  "admin", "api", "app", "www", "redy", "dashboard", "login", "signup",
  "onboarding", "pricing", "hardware", "privacy", "terms", "sitemap",
  "robots", "kitchen", "pos", "settings", "menu", "orders", "reports",
  "employees", "customers", "support", "help", "blog", "about", "contact",
  "features", "solution", "careers", "press", "display", "receipt", "order",
  "unsubscribed",
  "m", "buy", "guestlist", "tablet", "superadmin", "auth", "callback",
  "door", "employee", "venue", "design-system",
  "home", "me", "nuevo-negocio", "contador",
  // Rutas reales bajo app/ que faltaban (2026-07). Registrar una de estas crea
  // merchant+location pero su admin queda inalcanzable para siempre: la ruta
  // estática gana sobre app/[slug] y settings bloquea renombrar el slug.
  // ("reset-password" no tiene dir bajo app/ pero es PUBLIC_ROOT en proxy.ts.)
  "demo", "reserva", "review", "venezuela", "retail-preview",
  "checkout", "refund", "auth-link", "set-password", "verify-email",
  "forgot-password", "delete-my-account", "merchant-agreement",
  "sms-opt-in", "sms-terms", "sms-account-alerts", "reset-password",
  // Raíz de la página de marca (/marca/[slug]) — reservada ANTES de que la
  // ruta exista, porque un negocio que registre este slug queda con su admin
  // inalcanzable de forma permanente.
  "marca",
  // Raíz de la cuenta del comensal (/cuenta, /cuenta/claim, /cuenta/salir) en
  // redytoeat.com. Reservada ANTES de que la ruta exista, por el mismo motivo
  // que `marca`: un negocio con este slug queda con su admin inalcanzable y,
  // en el dominio consumer, pisa la cuenta.
  "cuenta",
  // Raíz del seguimiento del pedido (/p/<código>) en redytoeat.com. Hoy la
  // regex no deja slugs de 1 carácter; se reserva igual porque la ruta existe.
  "p",
  // Raíz de las integraciones (/integraciones/quickbooks/…). Intuit guarda
  // URLs FIJAS para desconectar y reconectar —no aceptan el slug de cada
  // local—, así que viven acá arriba. Un negocio con este slug quedaría con
  // su admin inalcanzable: la ruta estática gana sobre app/[slug].
  "integraciones",
  // Rutas estáticas del programa de referidos (/partners/*, /ref/[code]):
  // la ruta estática gana sobre app/[slug], así que un negocio con este slug
  // quedaría con su admin inalcanzable.
  "ref", "partners",
  // Raíz de las fuentes de las etiquetas de estante (/fonts/labels/*.ttf, en
  // public/fonts). Es PUBLIC_ROOT en proxy.ts: un negocio con este slug
  // quedaría con su admin inalcanzable.
  "fonts",
  // Segundo segmento de /<slug>/refund-policy (#1456, política de devoluciones
  // que pide North). Como slug de negocio haría ambigua /refund-policy en un
  // host dedicado. Verificado en prod 2026-11-03: 0 locales/merchants/alias/marcas.
  "refund-policy",
])

// Reservados también por forma normalizada: "sign-up" o "designsystem" chocan
// con rutas reales al resolverse tolerante, así que se bloquean igual.
const RESERVED_NORM = new Set([...RESERVED_SLUGS].map(normalizeSlug))

export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug) || RESERVED_NORM.has(normalizeSlug(slug))
}

export type SlugValidation = { ok: true } | { ok: false; reason: "format" | "reserved" }

export function validateSlug(slug: string): SlugValidation {
  if (!SLUG_REGEX.test(slug)) return { ok: false, reason: "format" }
  // Degenerados que el regex (bordes alfanuméricos) aún deja pasar: "ab--cd" y
  // "a-b". La identidad normalizada (sin guiones) debe seguir siendo un slug
  // dictable de ≥3 chars.
  if (slug.includes("--")) return { ok: false, reason: "format" }
  if (normalizeSlug(slug).length < 3) return { ok: false, reason: "format" }
  if (isReservedSlug(slug)) return { ok: false, reason: "reserved" }
  return { ok: true }
}

/** Variante para el alta asistida por superadmin: mismos reservados (también
 *  en forma normalizada), formato laxo (2-50). NO usar en superficies
 *  públicas — ahí va validateSlug. */
export function validateAgentSlug(slug: string): SlugValidation {
  if (!AGENT_SLUG_REGEX.test(slug)) return { ok: false, reason: "format" }
  if (isReservedSlug(slug)) return { ok: false, reason: "reserved" }
  return { ok: true }
}
